import BackgroundDynamic from '@activebrands/core-web/components/BackgroundDynamic';
import Link from '@activebrands/core-web/components/Link';
import media from '@activebrands/core-web/config/media';
import RichText from '@activebrands/core-web/libs/storyblok/RichText';
import { BackgroundOverlay, ButtonData, StoryblokEditable } from '@activebrands/core-web/libs/storyblok/storyblok';
import { TransformedBackgroundTypes } from '@activebrands/core-web/libs/storyblok/utils/transform-flexible-background';
import { HeadingType } from '@activebrands/core-web/libs/storyblok/utils/transform-heading';
import { Layout } from '@activebrands/core-web/libs/storyblok/utils/transform-layout';
import { ParagraphType } from '@activebrands/core-web/libs/storyblok/utils/transform-paragraph';
import {
    CmsHeadingStyle,
    CmsSmMdHeadingStyle,
    isCmsSmMdHeadingStyle,
} from '@activebrands/core-web/libs/storyblok/content/cms-module-content';
import { TagType } from '@activebrands/core-web/libs/storyblok/utils/transform-tag';
import { styled, useStyletron } from '@activebrands/core-web/libs/styletron';
import { StyletronStyle } from '@activebrands/core-web/types/styletron';
import getSelector from '@activebrands/core-web/utils/get-selector';
import getPositionStyles from '@activebrands/core-web/utils/style-functions/get-position-styles';
import getTextColorStyles from '@activebrands/core-web/utils/style-functions/get-text-color-styles';
import textStyles, { TextStyleKey } from 'config/branding/textStyles';
import ButtonsList from 'components/buttons/ButtonsList';
import ThemeButton from 'components/buttons/ThemeButton';
import PlayIcon from 'components/icons/PlayIcon';
import Heading from 'components/text/Heading';
import Tag from 'components/text/Tag';

const textBreakStyles = { minWidth: 0, overflowWrap: 'break-word' };

const Wrapper = styled('div', {
    position: 'relative',
    height: '100%',
    margin: '0 auto',
});

const ContentWrapper = styled('div', {
    ...textBreakStyles,
    position: 'relative',
    height: '100%',
    padding: ['24px 16px', null, null, null, null, '24px'],
});

const MaxWidthWrapper = styled('div', {
    margin: '0 auto',
    position: 'relative',
    width: '100%',
});

const ContentBox = styled('div', {
    width: '100%',
    position: 'relative',
    alignContent: 'space-between',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    textAlign: 'inherit',
    zIndex: 1,
    ...textBreakStyles,

    [media.min['tablet.sm']]: {
        width: '80%',
    },

    [media.min['desktop.sm']]: {
        width: '66%',
    },
});

type FontKeyArray = (TextStyleKey | null)[];
type HeadingStyleMap = Record<CmsSmMdHeadingStyle, FontKeyArray>;

const headingFontKeys: HeadingStyleMap = {
    sm: ['Primary/24_120_-1', 'Primary/32_100_-2', null, null, 'Primary/40_110_-2'],
    md: ['Primary/24_120_-1', 'Primary/40_110_-2', null, null, 'Primary/64_100_-4'],
};

const getHeadingFontKeys = (headingStyle?: CmsHeadingStyle): FontKeyArray | undefined => {
    if (!headingStyle || !isCmsSmMdHeadingStyle(headingStyle)) {
        return undefined;
    }

    return headingFontKeys[headingStyle];
};

const buttonListStyling: Partial<Record<Layout, StyletronStyle>> = {
    oneColumn: { flexWrap: 'wrap' },
    twoColumns: { flexWrap: 'wrap', flexDirection: ['row', null, 'column', null, 'row'], alignItems: 'flex-start' },
    threeColumns: { flexDirection: 'column' },
};

const contentButtonStyling: Record<string, string> = {
    black: 'inverted',
    white: 'default',
};

interface FlexibleProps {
    background?: TransformedBackgroundTypes[];
    backgroundLoading?: 'auto' | 'lazy' | 'eager';
    backgroundOverlay?: BackgroundOverlay[];
    buttons?: ButtonData[];
    caption?: string;
    contentHorizontalAlignment?: 'left' | 'right' | 'center';
    contentVerticalAlignment?: 'top' | 'bottom' | 'center';
    heading?: HeadingType;
    id?: string;
    isHero?: boolean;
    layout?: Layout;
    optiId?: string;
    paragraph?: ParagraphType;
    playButtonCallback?: () => void;
    playButtonLabel?: string;
    sbEditable?: StoryblokEditable;
    selector?: string;
    tag?: TagType;
    textColor?: string;
    wrapperLink?: string;
}

const Flexible = ({
    background,
    backgroundLoading = 'eager',
    backgroundOverlay,
    buttons = [],
    caption,
    contentHorizontalAlignment = 'center',
    contentVerticalAlignment = 'center',
    heading,
    id,
    isHero,
    layout = 'oneColumn',
    optiId,
    paragraph,
    playButtonCallback,
    playButtonLabel,
    sbEditable,
    selector,
    tag,
    textColor = 'black',
    wrapperLink,
}: FlexibleProps) => {
    const [css] = useStyletron();
    const hasButtons = buttons.length > 0;

    const textColorStyle = getTextColorStyles(textColor);
    const positionStyles = getPositionStyles({ contentHorizontalAlignment, contentVerticalAlignment });

    const buttonListStyles = buttonListStyling[layout];
    const isOneColumn = layout === 'oneColumn';
    const isThreeColumns = layout === 'threeColumns';
    const moduleHeadingLabel = heading?.text?.trim();
    const contextualButtons = buttons.map((button, index) => ({
        ...button,
        ariaLabel:
            index === 0 && moduleHeadingLabel && button?.label
                ? `${button.label}: ${moduleHeadingLabel}`
                : button?.ariaLabel,
    }));
    const playButtonAriaLabel =
        playButtonCallback && moduleHeadingLabel && playButtonLabel
            ? `${playButtonLabel}: ${moduleHeadingLabel}`
            : undefined;
    const wrapperLinkAriaLabel = wrapperLink
        ? moduleHeadingLabel ||
          tag?.text?.trim?.() ||
          (typeof caption === 'string' ? caption.trim() : undefined) ||
          undefined
        : undefined;
    const headingFontStyles = getHeadingFontKeys(heading?.style);

    return (
        <Wrapper
            $style={{ width: isHero || !isOneColumn ? '100%' : 'calc(100% - var(--margin-content) * 2)' }}
            data-selector={selector}
            data-opti-id={optiId}
            id={id}
            {...sbEditable}
        >
            <Link aria-label={wrapperLinkAriaLabel} className={css(textColorStyle)} to={wrapperLink}>
                <BackgroundDynamic
                    background={background}
                    backgroundOverlay={backgroundOverlay}
                    loading={backgroundLoading}
                    selector={getSelector(selector, 'media')}
                />
                <ContentWrapper data-color-scheme="light" $style={textColorStyle}>
                    {tag?.text && (
                        <div
                            className={css({
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                backgroundColor: 'var(--color-bg-flexible-tag)',
                                height: '24px',
                                color: 'var(--color-text-tag)',
                            })}
                        >
                            <Tag data-selector={getSelector(selector, 'eyebrow')} {...tag.sbEditable}>
                                {tag.text}
                            </Tag>
                        </div>
                    )}
                    <MaxWidthWrapper $style={positionStyles}>
                        <ContentBox
                            $style={{
                                marginTop: isHero && contentVerticalAlignment === 'top' ? 'var(--height-header)' : '0',
                            }}
                        >
                            {playButtonCallback && (
                                <div>
                                    <ThemeButton
                                        $style={{
                                            display: 'flex',
                                            gap: '0 8px',
                                            alignItems: 'center',
                                            padding: '12px',
                                        }}
                                        fontKeys="Primary/16_100_-05"
                                        theme={contentButtonStyling[textColor]}
                                        aria-label={playButtonAriaLabel}
                                        data-selector={getSelector(selector, 'cta')}
                                        onClick={playButtonCallback}
                                    >
                                        {playButtonLabel}
                                        <PlayIcon />
                                    </ThemeButton>
                                </div>
                            )}
                            {heading?.text && (
                                <Heading
                                    as={heading.type}
                                    $style={{ textAlign: contentHorizontalAlignment }}
                                    data-selector={getSelector(selector, 'title')}
                                    fontKeys={headingFontStyles}
                                    sbEditable={heading.sbEditable}
                                >
                                    {heading.text}
                                </Heading>
                            )}
                            {paragraph?.text && (
                                <div
                                    className={css({
                                        textAlign: contentHorizontalAlignment,
                                        ...textStyles['Primary/16_140_-05'],
                                    })}
                                    data-selector={getSelector(selector, 'body')}
                                    {...paragraph.sbEditable}
                                >
                                    <RichText data={paragraph.text} />
                                </div>
                            )}
                            {hasButtons && (
                                <ButtonsList
                                    buttonStyling={{ width: isThreeColumns ? '100%' : 'auto' }}
                                    $style={{
                                        gap: '16px',
                                        ...positionStyles,
                                        ...buttonListStyles,
                                    }}
                                    buttonSelector={getSelector(selector, 'cta')}
                                    buttons={contextualButtons}
                                />
                            )}
                        </ContentBox>
                    </MaxWidthWrapper>
                </ContentWrapper>
            </Link>
        </Wrapper>
    );
};

export default Flexible;
